import React, { useState } from "react";

import {
  ABILITY_KEYS,
  ABILITY_NAMES,
  buildFoundryJSON,
  calculateHP,
  getAttributeTotal,
  getCharacterLevel,
  getExportFileBaseName,
  getFullName,
  getSubtitle,
  modStr,
} from "../characterCreatorUtils.js";

const LS_KEY = "worldhub_characters";

function loadCharacters() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function deleteCharacter(index) {
  const chars = loadCharacters();
  chars.splice(index, 1);
  localStorage.setItem(LS_KEY, JSON.stringify(chars));

function duplicateCharacter(index) {
  const chars = loadCharacters();
  const copy = JSON.parse(JSON.stringify(chars[index]));
  chars.push(copy);
  localStorage.setItem(LS_KEY, JSON.stringify(chars));
}
}

// ── Character Detail Modal ───────────────────────────────────

function CharDetailModal({ snapshot, onClose, onEdit, onDuplicate, onExport }) {
  const [showJson, setShowJson] = useState(false);
  const name = getFullName(snapshot, "Herói sem nome");
  const subtitle = getSubtitle(snapshot);
  const level = getCharacterLevel(snapshot);
  const hp = calculateHP(snapshot);

  return (
    <div className="char-detail-overlay" onClick={onClose}>
      <div className="char-detail-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="char-detail-close" onClick={onClose} aria-label="Fechar">✕</button>

        <header className="char-detail-header">
          <div className="char-detail-portrait">
            <span className="char-detail-initial">{name.charAt(0).toUpperCase()}</span>
          </div>
          <div className="char-detail-header-text">
            <h2 className="char-detail-name">{name}</h2>
            <p className="char-detail-subtitle">{subtitle}</p>
            <div className="char-detail-stats">
              <span className="char-card-stat">Nível {level}</span>
              {hp > 0 && <span className="char-card-stat char-card-stat--hp">❤ {hp} HP</span>}
            </div>
          </div>
        </header>

        <h3 className="char-detail-section-title">Atributos</h3>
        <div className="char-detail-attrs">
          {ABILITY_KEYS.map((key) => {
            const total = getAttributeTotal(snapshot, key);
            return (
              <div key={key} className="char-detail-attr">
                <div className="char-detail-attr-label">{ABILITY_NAMES[key].slice(0, 3).toUpperCase()}</div>
                <div className="char-detail-attr-val">{total}</div>
                <div className="char-detail-attr-mod">{modStr(total)}</div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="char-detail-json-toggle"
          onClick={() => setShowJson((v) => !v)}
        >
          {showJson ? "▲ Ocultar JSON" : "▼ Ver dados brutos (JSON)"}
        </button>

        {showJson && (
          <pre className="char-detail-json">{JSON.stringify(snapshot, null, 2)}</pre>
        )}

        <div className="char-detail-actions">
          <button type="button" className="wiz-btn" onClick={onClose}>Fechar</button>
          <button type="button" className="wiz-btn" onClick={onDuplicate}>⧉ Duplicar</button>
          <button type="button" className="wiz-btn" onClick={onExport}>⬇ Exportar JSON</button>
          <button type="button" className="wiz-btn primary" onClick={onEdit}>✏ Editar</button>
        </div>
      </div>
    </div>
  );
}

// ── Character Card ───────────────────────────────────────────

function CharCard({ snapshot, index, onDelete, onOpen }) {
  const name = getFullName(snapshot, "Herói sem nome");
  const subtitle = getSubtitle(snapshot);
  const level = getCharacterLevel(snapshot);
  const hp = calculateHP(snapshot);

  return (
    <div
      className="char-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
    >
      <div className="char-card-portrait" aria-hidden="true">
        <span className="char-card-initial">{name.charAt(0).toUpperCase()}</span>
      </div>
      <div className="char-card-body">
        <h3 className="char-card-name">{name}</h3>
        <p className="char-card-subtitle">{subtitle}</p>
        <div className="char-card-stats">
          <span className="char-card-stat">Nível {level}</span>
          {hp > 0 && <span className="char-card-stat char-card-stat--hp">❤ {hp} HP</span>}
        </div>
      </div>
      <button
        type="button"
        className="char-card-delete"
        title="Excluir personagem"
        onClick={(e) => { e.stopPropagation(); onDelete(index); }}
      >
        ✕
      </button>
    </div>
  );
}

// ── Hub ──────────────────────────────────────────────────────

export default function CharacterHub({ onNewCharacter, onEdit }) {
  const [characters, setCharacters] = useState(loadCharacters);
  const [openIdx, setOpenIdx] = useState(null);

  function handleDelete(index) {
    if (!confirm(`Excluir "${getFullName(characters[index], "este personagem")}"?`)) return;
    deleteCharacter(index);
    setCharacters(loadCharacters());
  }

  function handleEdit(index) {
    setOpenIdx(null);
    onEdit(index, characters[index]);

    function handleExport(index) {
      setOpenIdx(null);
      try {
        const json = JSON.stringify(buildFoundryJSON(characters[index]), null, 2);
        const filename = getExportFileBaseName(characters[index]) + ".json";
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Erro ao exportar personagem:", e);
      }
    }

    function handleDuplicate(index) {
      setOpenIdx(null);
      duplicateCharacter(index);
      setCharacters(loadCharacters());
    }
  }

  return (
    <div className="char-hub">
      {openIdx !== null && (
        <CharDetailModal
          snapshot={characters[openIdx]}
          onClose={() => setOpenIdx(null)}
          onEdit={() => handleEdit(openIdx)}
          onDuplicate={() => handleDuplicate(openIdx)}
          onExport={() => handleExport(openIdx)}
        />
      )}

      <header className="char-hub-header">
        <h1 className="char-hub-title">Seus Personagens</h1>
        <button type="button" className="wiz-btn primary char-hub-new-btn" onClick={onNewCharacter}>
          ✦ Criar novo personagem
        </button>
      </header>

      {characters.length === 0 ? (
        <div className="char-hub-empty">
          <p>Nenhum personagem criado ainda.</p>
          <button type="button" className="wiz-btn primary" onClick={onNewCharacter}>
            ✦ Criar meu primeiro personagem
          </button>
        </div>
      ) : (
        <div className="char-hub-grid">
          {characters.map((snapshot, i) => (
            <CharCard
              key={i}
              snapshot={snapshot}
              index={i}
              onDelete={handleDelete}
              onOpen={() => setOpenIdx(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
